/**
 * SoA creature store. Preallocated at MAX_AGENTS; slots are monotonic —
 * no death means no recycling, ever. DOM-free, three-free: runs in the
 * Worker and in Node (tests, seed harness).
 */
import { PARAMS } from './params';
import type { Rng } from '../core/rng';

export const BEHAVIOR = {
  IDLE: 0,
  SEEK: 1,
  SEEK_FOOD: 2,
  CHASE_BALL: 3,
  CELEBRATE: 4,
  EAT: 5,
  SLEEP: 6,
  DORMANT: 7,
  DANCE: 8, // broadcastable joy — spreads by contagion
  VISIT: 9, // drawn to a dormant friend; the throng keeps watch over its own
  SEEK_WATER: 10, // heading to a pond to splash about
  SPLASH: 11, // playing at the water's edge (a desire, not a need)
} as const;

/** Genome (0..255 each). Phenotype = gene/255; children mutate ±16. */
export const GENE = {
  HUE: 0, // lineage tint on the yellow base
  SIZE: 1, // adult scale
  SPEED: 2,
  SOCIABILITY: 3, // cohesion + contagion susceptibility
  CURIOSITY: 4, // wander radius
  PLAYFULNESS: 5, // fun decay up, play utility up
  GENE_COUNT: 6,
} as const;

/** Life-state ladder (no death — DORMANT is the floor, fully reversible). */
export const LIFE = {
  THRIVING: 0,
  CONTENT: 1,
  GLUM: 2,
  GRIMY: 3,
  DORMANT: 4,
} as const;

/** anim ids shared with the vertex shader */
export const ANIM = {
  IDLE: 0,
  HOP: 1,
  EAT: 2,
  SLEEP: 4,
  DORMANT: 5,
} as const;

export class ThrongStore {
  count = 0;

  // spatial
  readonly posX = new Float32Array(PARAMS.MAX_AGENTS);
  readonly posZ = new Float32Array(PARAMS.MAX_AGENTS);
  readonly velX = new Float32Array(PARAMS.MAX_AGENTS);
  readonly velZ = new Float32Array(PARAMS.MAX_AGENTS);
  readonly heading = new Float32Array(PARAMS.MAX_AGENTS); // radians, faces +Z at 0

  // render-facing
  readonly anim = new Uint8Array(PARAMS.MAX_AGENTS);
  readonly emotion = new Uint8Array(PARAMS.MAX_AGENTS).fill(255);
  readonly scaleQ = new Uint8Array(PARAMS.MAX_AGENTS); // scale * 200
  readonly seed = new Uint8Array(PARAMS.MAX_AGENTS);
  readonly phase = new Uint8Array(PARAMS.MAX_AGENTS);

  // behavior
  readonly behavior = new Uint8Array(PARAMS.MAX_AGENTS);
  readonly behaviorTimer = new Uint16Array(PARAMS.MAX_AGENTS); // ticks
  readonly targetX = new Float32Array(PARAMS.MAX_AGENTS);
  readonly targetZ = new Float32Array(PARAMS.MAX_AGENTS);
  readonly targetId = new Uint16Array(PARAMS.MAX_AGENTS); // item id for SEEK_FOOD

  // needs, 0..255 (255 = fully satisfied); float for fractional decay
  readonly hunger = new Float32Array(PARAMS.MAX_AGENTS);
  readonly clean = new Float32Array(PARAMS.MAX_AGENTS);
  readonly fun = new Float32Array(PARAMS.MAX_AGENTS);
  readonly rest = new Float32Array(PARAMS.MAX_AGENTS);

  // life-state ladder + growth
  readonly lifeState = new Uint8Array(PARAMS.MAX_AGENTS);
  readonly lowMoodSec = new Uint16Array(PARAMS.MAX_AGENTS); // time spent under the next threshold
  readonly ageSec = new Uint32Array(PARAMS.MAX_AGENTS);
  readonly splitCharge = new Float32Array(PARAMS.MAX_AGENTS); // 0..1
  readonly splitCooldown = new Uint16Array(PARAMS.MAX_AGENTS); // seconds
  readonly wakeProgress = new Float32Array(PARAMS.MAX_AGENTS); // dormant scrub revival
  readonly familiarity = new Uint8Array(PARAMS.MAX_AGENTS); // personal bond (petting)
  readonly circadian = new Float32Array(PARAMS.MAX_AGENTS); // phase offset ±0.15

  // stable per-creature movement traits
  readonly speedMul = new Float32Array(PARAMS.MAX_AGENTS);

  // genome: GENE_COUNT interleaved u8 planes [agent * GENE_COUNT + gene]
  readonly genes = new Uint8Array(PARAMS.MAX_AGENTS * GENE.GENE_COUNT);

  gene(i: number, g: number): number {
    return this.genes[i * GENE.GENE_COUNT + g] as number;
  }

  /** Phenotype in [0, 1]. */
  pheno(i: number, g: number): number {
    return (this.genes[i * GENE.GENE_COUNT + g] as number) / 255;
  }

  spawn(x: number, z: number, rng: Rng): number {
    const i = this.count++;
    this.posX[i] = x;
    this.posZ[i] = z;
    this.heading[i] = rng.range(0, Math.PI * 2);
    this.seed[i] = rng.int(256);
    this.phase[i] = rng.int(256);
    this.scaleQ[i] = 200; // adult 1.0 for now; juveniles arrive with mitosis
    this.speedMul[i] = rng.range(0.85, 1.15);
    this.behavior[i] = BEHAVIOR.IDLE;
    this.behaviorTimer[i] = rng.int(90); // stagger first decisions
    this.hunger[i] = rng.range(215, 245);
    this.clean[i] = rng.range(215, 245);
    this.fun[i] = rng.range(215, 245);
    this.rest[i] = rng.range(215, 245);
    this.lifeState[i] = 1; // CONTENT
    this.circadian[i] = rng.range(-0.15, 0.15);
    for (let g = 0; g < GENE.GENE_COUNT; g++) {
      this.genes[i * GENE.GENE_COUNT + g] = 100 + rng.int(56); // founders near the middle
    }
    this.speedMul[i] = 0.8 + this.pheno(i, GENE.SPEED) * 0.5;
    return i;
  }

  /** Mitosis: child pops out beside the parent, juvenile-sized, genes ±16. */
  spawnChild(parent: number, rng: Rng): number {
    const a = rng.range(0, Math.PI * 2);
    const i = this.spawn(
      (this.posX[parent] as number) + Math.sin(a) * 0.7,
      (this.posZ[parent] as number) + Math.cos(a) * 0.7,
      rng,
    );
    for (let g = 0; g < GENE.GENE_COUNT; g++) {
      let v = this.genes[parent * GENE.GENE_COUNT + g] as number;
      if (rng.next() < 0.35) {
        v += rng.next() < 0.5 ? -16 : 16;
        v = v < 0 ? 0 : v > 255 ? 255 : v;
      }
      this.genes[i * GENE.GENE_COUNT + g] = v;
    }
    this.speedMul[i] = 0.8 + this.pheno(i, GENE.SPEED) * 0.5;
    this.scaleQ[i] = 110; // JUVENILE_SCALE_Q
    this.hunger[i] = 200;
    this.clean[i] = 210;
    this.fun[i] = 210;
    this.rest[i] = 200;
    return i;
  }
}
